import { SelectOption } from '@a-ui/react';
import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { translate } from '../i18n';
import { ITranslations } from '../i18n/config';
import { AuthenticationState } from '../state/Authentication/reducer';

export enum PersonalDataField {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  NICKNAME = 'nickname',
  GENDER = 'gender',
  BIRTH_DATE = 'birthDate',
}

export const TRANSLATIONS: Partial<Record<keyof Api.User, ITranslations>> = {
  [PersonalDataField.FIRST_NAME]: 'personal_data.content.first_name',
  [PersonalDataField.LAST_NAME]: 'personal_data.content.last_name',
  [PersonalDataField.NICKNAME]: 'personal_data.content.nickname',
  [PersonalDataField.GENDER]: 'personal_data.content.gender',
  [PersonalDataField.BIRTH_DATE]: 'personal_data.content.birthdate',
};

export const GENDER_OPTIONS: SelectOption[] = [
  {
    value: 'm',
    label: translate('common.gender.male'),
  },
  {
    value: 'v',
    label: translate('common.gender.female'),
  },
];

export const CRS_PERSOON_KEYMAP: Partial<Record<keyof Api.User, keyof Api.CrsPersoon>> = {
  birthDate: 'birthDate',
  lastName: 'familyName',
  gender: 'genderCode',
  firstName: 'givenName',
};

const usePersonalData = (state: AuthenticationState, fields: PersonalDataField[]) => {
  const { user, crsPersoon, eID } = state;

  const hasEID = React.useMemo(() => {
    if (eID?.rrnr && eID?.rrnr !== null) {
      return true;
    }

    return false;
  }, [eID]);

  const data: Record<string, string | number | boolean | Date | undefined> = useMemo(() => {
    return fields.reduce((result, _key) => {
      const key = _key as keyof Api.User;
      let value =
        hasEID && crsPersoon && Object.keys(CRS_PERSOON_KEYMAP).includes(key)
          ? crsPersoon[CRS_PERSOON_KEYMAP[key]!]
          : user[key];

      if (key === 'gender') {
        value = (value as string)?.toLowerCase();
      }

      if (hasEID && crsPersoon && key === 'birthDate') {
        const birthDateValue = value as string;

        // Handle dates in YYYYMMDD format (e.g., "19620000")
        if (birthDateValue && birthDateValue.length === 8) {
          const year = birthDateValue.substring(0, 4);
          const month = birthDateValue.substring(4, 6);
          const day = birthDateValue.substring(6, 8);

          // If date contains 00 for day/month, keep them; otherwise parse with dayjs
          if (month === '00' || day === '00') {
            value = `${day}/${month}/${year}`;
          } else {
            value = dayjs(`${year}-${month}-${day}`).format('DD/MM/YYYY');
          }
        } else if (birthDateValue) {
          value = dayjs(birthDateValue).format('DD/MM/YYYY');
        }
      }

      return {
        ...result,
        [key]: value,
      };
    }, {});
  }, [user]);

  const humanReadableData: Record<string, string | number> = useMemo(() => {
    return Object.keys(data).reduce((result, _key) => {
      const key = _key as keyof Api.User;
      let value = data[key];

      if (key === 'gender') {
        value = GENDER_OPTIONS.find(({ value: v }) => v === value)?.label;
      }

      return {
        ...result,
        [translate(TRANSLATIONS[key]!)]: value,
      };
    }, {});
  }, [data]);

  return { data, humanReadableData, hasEID };
};

export default usePersonalData;
